import { App, Form } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CustomModal } from "sipli-fleet-core/components";
import { useMutationUpdateWarehouseUnit } from "src/queries/warehouseUnits.queries";
import type { WarehouseUnitRecord } from "src/services/warehouseUnitService";
import UnitInfoFields from "./UnitInfoFields";
import { useIsFuelSensor, type UnitFormValues } from "./unitFormShared";

interface EditUnitModalProps {
  open: boolean;
  record: WarehouseUnitRecord | null;
  onClose: () => void;
  onUpdated?: () => void;
}

const UNIT_INFO_BASE_FIELDS: (keyof UnitFormValues)[] = ["unitTypeId", "modelId", "serial"];
const DEVICE_ONLY_FIELDS: (keyof UnitFormValues)[] = ["imei"];

const getRecordActive = (record: WarehouseUnitRecord) => {
  if (typeof record.active === "boolean") return record.active;
  if (typeof record.active === "number") return record.active === 1;
  if (record.active && typeof record.active === "object" && "id" in record.active) {
    return Boolean((record.active as { id?: unknown }).id);
  }
  return false;
};

const EditUnitModal = ({ open, record, onClose, onUpdated }: EditUnitModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UnitFormValues>();
  const isFuelSensor = useIsFuelSensor(form, open);
  const { mutateAsync: updateUnit, isPending: isUpdating } = useMutationUpdateWarehouseUnit();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (!record) return;

    const hwStatus = record.hardwareStatus ?? record.unitStatus;

    form.setFieldsValue({
      unitTypeId: record.unitType?.id,
      makeId: record.make?.id,
      modelId: record.model?.id,
      imei: record.imei ?? undefined,
      serial: record.serial ?? undefined,
      phoneNumber: record.phoneNumber ?? undefined,
      length: typeof record.length === "string" ? record.length : undefined,
      unitStatusId: hwStatus?.id,
      active: getRecordActive(record) ? "true" : "false",
      operationTypeId: record.operationType?.id,
    });
  }, [open, record, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async () => {
    if (!record) return;

    const fields = isFuelSensor
      ? UNIT_INFO_BASE_FIELDS
      : [...UNIT_INFO_BASE_FIELDS, ...DEVICE_ONLY_FIELDS];

    try {
      await form.validateFields(fields);
      const values = form.getFieldsValue(true);

      const payload = {
        modelId: values.modelId as string | number,
        unitTypeId: values.unitTypeId as string | number,
        serial: values.serial,
        unitStatusId: values.unitStatusId,
        operationTypeId: values.operationTypeId as string | number,
        ...(isFuelSensor
          ? { length: values.length }
          : {
              imei: values.imei,
              phoneNumber: values.phoneNumber,
              active: values.active === "true",
            }),
      };

      await updateUnit({ id: record.id, payload });
      message.success(t("unit_updated_successfully"));
      onUpdated?.();
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        message.error(t("failed_to_update_unit"));
      }
    }
  };

  return (
    <CustomModal
      open={open}
      onCancel={handleClose}
      okText={t("save")}
      cancelText={t("cancel")}
      onOk={handleSave}
      okBtnLoading={isUpdating}
      width={835}
      destroyOnHidden
      title={t("edit_unit_info")}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ active: "true" }}
        className="all-units-form"
      >
        <UnitInfoFields form={form} open={open} />
      </Form>
    </CustomModal>
  );
};

export default EditUnitModal;
